import { orgAPI } from "@/api/api";
import type { Email } from "@cohesive/api-entities";
import { useQuery } from "@tanstack/react-query";

interface UseEmailThreadFetchProps {
  integrationConnectionId?: string;
  threadId: string;
  enabled?: boolean;
}

// Parse Gmail thread response
function parseGmailThreadResponse(rawResponse: any): Email[] {
  if (!Array.isArray(rawResponse)) {
    throw new Error("Invalid response format: expected array");
  }

  // The response is an array of entity objects with structure:
  // [{type: 'gmail_email', base: 'email', platform: 'gmail', data: {...}}]
  const emails = rawResponse
    .filter(
      (item) =>
        item?.data && (item.base === "email" || item.type === "gmail_email"),
    )
    .map((entity: any) => entity.data);

  if (emails.length === 0) {
    throw new Error("No email entities found in response");
  }

  return emails;
}

export function useEmailThreadFetch({
  integrationConnectionId,
  threadId,
  enabled = true,
}: UseEmailThreadFetchProps) {
  return useQuery({
    queryKey: ["gmail-thread", integrationConnectionId, threadId],
    queryFn: async () => {
      if (!integrationConnectionId) {
        throw new Error("Missing integration connection ID");
      }

      const response = await orgAPI.toolbox.execute({
        integration_connection_id: integrationConnectionId,
        action_name: "gmail-thread-get-html",
        action_input: { thread_id: threadId },
      });

      if (!response.data) {
        throw new Error("Failed to fetch Gmail thread: No data returned");
      }

      return parseGmailThreadResponse(response.data);
    },
    enabled: !!integrationConnectionId && !!threadId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
